import React, { useState, useEffect } from 'react';
import TopNavigation from './TopNavigation';
import ClueRaiderBanner from './components/ClueRaiderBanner';
import gettingStartedImg from './fluxlogo.png';
import MailerLiteSignup from './components/MailerLiteSignup';
import tiktokIcon from './TikTok_Icon_Black_Circle.png';
import { updatePageSEO } from './utils/seo';

const About: React.FC = () => {
  const [showFullBackground, setShowFullBackground] = useState(false);

  useEffect(() => {
    updatePageSEO(
      'About Dimension Flux - Daily Inter-Dimensional Logic Puzzles',
      'Learn about Dimension Flux, a daily logic puzzle game where you solve inter-dimensional mysteries by eliminating suspects and following cryptic clues.'
    );
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Top Navigation */}
      <TopNavigation />
      <ClueRaiderBanner />

      <div className="p-2 sm:p-4">
        {/* Header with logo */}
        <div className="flex justify-center mb-2">
          <div className="w-full md:max-w-xl">
            {/* Desktop: Logo with subtitle overlay */}
            <div className="relative">
              <img src={gettingStartedImg} alt="Dimension Flux" style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 4px 32px 0 rgba(80, 0, 120, 0.10)', objectFit: 'contain', objectPosition: 'center' }} />
            </div>
          </div>
        </div>

        {/* After the header image */}
        <div className="flex justify-center">
          <div className="w-full max-w-xl">
            {/* NEW ABOUT CONTENT */}
            <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-6 border-2 border-gray-300 shadow-lg mb-4 sm:mb-6 w-full">
              <div className="text-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-oswald font-bold text-gray-900 mb-4">About Dimension Flux</h1>
                <p className="text-base font-lora text-gray-700 italic">A new puzzle every day. Strange news, dark humour, and pure logic.</p>
              </div>
              
              <div className="space-y-6 text-gray-800 font-lora text-sm sm:text-base leading-relaxed">
                <p>
                  Dimension Flux is a daily deduction game disguised as a supernatural newsfeed. Each morning brings a bizarre incident — folklore creatures in modern settings, armed with strange inter-dimensional weapons. The reports are written with a wink of dark humour, but behind the satire lies a logic puzzle you can actually solve.
                </p>

                <p>
                  Your task: stop the incursion. Work out which Being crossed over, what Weapon they wielded, and where they're hiding. Four logical clues — plus one cryptic hidden hint in the story — give you everything you need. No guessing, no dice, no luck. Just you, your logic, and the flux.
                </p>

                <div className="text-center pt-0 pb-2">
                  <button 
                    onClick={() => setShowFullBackground(!showFullBackground)}
                    className="px-6 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-semibold transition-colors duration-200"
                  >
                    {showFullBackground ? 'Show Less' : 'Read More'}
                  </button>
                </div>

                {showFullBackground && (
                  <div className="border-t border-gray-300 pt-6">
                    <h2 className="text-xl font-oswald font-bold text-gray-900 mb-4">THE BOUNDARY BETWEEN WORLDS</h2>
                    
                    <div className="space-y-4">
                      <p>
                        Dimension Flux is the unstable boundary between worlds — a shifting current where reality bends, and stories take shape. On the other side lies a twisted echo of Earth, built from human fears, myths, and legends.
                      </p>

                      <p>
                        A dying alien civilization discovered this echo-realm. They armed its native beings with strange inter-dimensional weapons and pushed them through into our world. Each incursion looks like chaos — giants in city streets, witches stealing dogs, zombies in swimming pools — but in truth, these beings are building pieces of a vast portal array that could open Earth to invasion.
                      </p>

                      <h3 className="text-base font-oswald font-semibold text-gray-900 mt-6 mb-3">Bureau Gamma</h3>
                      <p>
                        That's where you come in. Bureau Gamma, a shadow agency monitoring dimensional breaches, delivers each incident to you disguised as a satirical case file. Your job: cut through the absurdity, eliminate suspects, and identify the right Being, Weapon, and Hideout before the breach widens.
                      </p>

                      <h3 className="text-base font-oswald font-semibold text-gray-900 mt-6 mb-3">Why the Name</h3>
                      <p>
                        <strong>Dimension:</strong> the parallel stratum warped by human folklore.
                      </p>
                      <p>
                        <strong>Flux:</strong> instability, constant change, the bleeding of stories into reality.
                      </p>
                      <p>
                        Together, Dimension Flux is the shifting boundary where only logic holds the line.
                      </p>

                      <h3 className="text-base font-oswald font-semibold text-gray-900 mt-6 mb-3">What to Expect</h3>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li><strong>Daily puzzles:</strong> one fresh case every day.</li>
                        <li><strong>Folklore meets logic:</strong> creatures from legend armed with impossible weapons.</li>
                        <li><strong>Stars & affinities:</strong> every suspect has a power rating (1–5 stars) and a planetary affinity.</li>
                        <li><strong>Hidden clues:</strong> each story hides a cryptic wordplay hint pointing to part of the solution.</li>
                        <li><strong>Pure deduction:</strong> no dice, no luck — just your reasoning.</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="mt-6 mb-6">
              <MailerLiteSignup />
            </div>

            {/* Always visible closing message */}
            <div className="text-center mt-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
              <p className="text-lg font-oswald font-semibold text-gray-700">
                Solve today's case. Seal the breach. Return tomorrow. The flux never stops.
              </p>
            </div>

            {/* Video Demo Link */}
            <div className="text-center mt-4">
              <a 
                href="https://www.tiktok.com/@dimension_flux/video/7561503088922692886" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                See It in Action
              </a>
            </div>

            {/* Social Links */}
            <div className="text-center mt-4 mb-2">
              <a href="https://x.com/Dimension_Flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 text-white hover:opacity-80" aria-label="X profile">
                <svg className="w-5 h-5 inline-block align-middle" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2H21L13.5 10.5 22.5 22H15.5L10.5 15.5 4.5 22H2L9.5 13.5 1.5 2H8.5L13.5 8.5 18.244 2Z"/>
                </svg>
              </a>
              <a href="https://www.tiktok.com/@dimension_flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 hover:opacity-80" aria-label="TikTok profile">
                <img src={tiktokIcon} alt="TikTok" className="w-5 h-5 inline-block align-middle rounded-full" />
              </a>
            </div>

            {/* Copyright + AI Disclosure */}
            <div className="text-center mt-2 text-sm text-white">
              © N.M.A. Pitman 2025 • Contact: <a href="mailto:hello@dimensionflux.com" className="text-white underline">hello@dimensionflux.com</a>
              <div className="mt-1 text-xs opacity-90"><a href="https://www.vecteezy.com/free-vector/portal">Portal Vectors by Vecteezy</a></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
